import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatINR, formatDateTime } from "@/lib/format";
import {
  Loader2, Search, FileStack, Clock, BadgeCheck, XCircle, Banknote, ReceiptIndianRupee, TrendingUp, BellRing, RefreshCw,
} from "lucide-react";

const STAT_CARDS = [
  { key: "total", label: "Total Applications", Icon: FileStack, color: "text-slate-700 bg-slate-100" },
  { key: "pending", label: "Pending / Review", Icon: Clock, color: "text-sky-700 bg-sky-100" },
  { key: "approved", label: "Approved", Icon: BadgeCheck, color: "text-emerald-700 bg-emerald-100" },
  { key: "rejected", label: "Rejected", Icon: XCircle, color: "text-rose-700 bg-rose-100" },
  { key: "disbursed", label: "Disbursed", Icon: Banknote, color: "text-emerald-700 bg-emerald-100" },
  { key: "active_repayments", label: "Active Repayments", Icon: ReceiptIndianRupee, color: "text-indigo-700 bg-indigo-100" },
  { key: "completed_repayments", label: "Completed Repayments", Icon: BadgeCheck, color: "text-blue-700 bg-blue-100" },
  { key: "repayment_verification_pending", label: "Verify Pending", Icon: Clock, color: "text-amber-700 bg-amber-100" },
];

const STATUSES = [
  { value: "ALL", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "DISBURSED", label: "Disbursed" },
  { value: "REPAID", label: "Repaid" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const knownIds = useRef(new Set());

  const loadApps = useCallback(async (query, st, markKnown = false) => {
    const { data } = await api.get("/admin/applications", { params: { q: query || undefined, status: st } });
    if (markKnown) knownIds.current = new Set(data.map((a) => a.id));
    setApps(data);
    return data;
  }, []);

  const refreshStats = useCallback(async () => {
    const s = await api.get("/admin/stats");
    setStats(s.data);
  }, []);

  useEffect(() => {
    (async () => {
      await refreshStats();
      await loadApps("", "ALL", true);
      setLoading(false);
    })();
  }, [refreshStats, loadApps]);

  useEffect(() => {
    const t = setTimeout(() => loadApps(q, status, true), 300);
    return () => clearTimeout(t);
  }, [q, status, loadApps]);

  // Auto-refresh: poll for newly submitted applications every 12s (real DB, no mock).
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        await refreshStats();
        const data = await loadApps(q, status, false);
        const fresh = data.filter((a) => !knownIds.current.has(a.id));
        if (fresh.length) {
          setNewCount((c) => c + fresh.length);
          fresh.forEach((a) => knownIds.current.add(a.id));
        }
      } catch {}
    }, 12000);
    return () => clearInterval(id);
  }, [q, status, loadApps, refreshStats]);

  const manualRefresh = async () => {
    setRefreshing(true);
    try { await refreshStats(); await loadApps(q, status, true); setNewCount(0); }
    finally { setRefreshing(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Overview of all loan applications and activity.</p>
        </div>
        <div className="flex items-center gap-2">
          {newCount > 0 && (
            <button onClick={manualRefresh} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-1.5 animate-pulse" data-testid="new-application-indicator">
              <BellRing className="h-3.5 w-3.5" /> {newCount} new application{newCount > 1 ? "s" : ""} — click to view
            </button>
          )}
          <Button variant="outline" size="sm" onClick={manualRefresh} disabled={refreshing} data-testid="admin-refresh-button">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map((c) => (
          <Card key={c.key} className="p-4 ip-card-shadow" data-testid={`stat-${c.key}`}>
            <div className={`h-9 w-9 grid place-items-center rounded-lg ${c.color} mb-3`}><c.Icon className="h-4 w-4" /></div>
            <p className="text-2xl font-extrabold tracking-tight">{stats?.[c.key] ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.label}</p>
          </Card>
        ))}
        <Card className="p-4 ip-card-shadow col-span-2 sm:col-span-1" data-testid="stat-volume">
          <div className="h-9 w-9 grid place-items-center rounded-lg text-gold bg-amber-100 mb-3"><TrendingUp className="h-4 w-4" /></div>
          <p className="text-2xl font-extrabold tracking-tight font-mono">{formatINR(stats?.total_disbursed_volume)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Disbursed Volume</p>
        </Card>
      </div>

      <Card className="p-5 ip-card-shadow">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, name, email, phone or Customer ID" className="pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-56" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2.5 pr-4 font-medium">Reference</th>
                <th className="py-2.5 pr-4 font-medium">Customer</th>
                <th className="py-2.5 pr-4 font-medium hidden lg:table-cell">Type</th>
                <th className="py-2.5 pr-4 font-medium hidden md:table-cell">Income</th>
                <th className="py-2.5 pr-4 font-medium hidden sm:table-cell">Submitted</th>
                <th className="py-2.5 pr-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {apps.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No applications found.</td></tr>}
              {apps.map((a) => (
                <tr key={a.id} onClick={() => nav(`/admin/applications/${a.id}`)} className="border-b border-border/60 hover:bg-brand-light/50 cursor-pointer transition-colors" data-testid={`app-row-${a.reference_number}`}>
                  <td className="py-3 pr-4 font-mono font-semibold">{a.reference_number}</td>
                  <td className="py-3 pr-4">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.email}</div>
                  </td>
                  <td className="py-3 pr-4 hidden lg:table-cell">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${a.type === "Repeat" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{a.type === "Repeat" ? "Repeat Loan" : "New Loan"}</span>
                  </td>
                  <td className="py-3 pr-4 font-mono hidden md:table-cell">{formatINR(a.monthly_income)}</td>
                  <td className="py-3 pr-4 text-muted-foreground hidden sm:table-cell">{formatDateTime(a.submitted_at)}</td>
                  <td className="py-3 pr-4"><StatusBadge status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
